import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";

/**
 * AuthCallback — handles the `#session_id=…` URL fragment that the Emergent
 * Google OAuth flow returns us with. Exchanges it for our own JWT, then
 * redirects into the app.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { checkUserAuth } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      setError("No Google session was returned. Please try again.");
      return;
    }

    (async () => {
      try {
        await base44.auth.completeGoogleLogin(sessionId);
        // Clean up the hash from history before re-checking auth
        window.history.replaceState(null, "", "/");
        await checkUserAuth();
        navigate("/", { replace: true });
      } catch (e) {
        setError(e?.message || "Sign-in failed");
      }
    })();
  }, [navigate, checkUserAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="text-center max-w-sm" data-testid="auth-callback">
        {error ? (
          <>
            <p className="text-destructive font-semibold mb-2">Sign-in failed</p>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <button
              className="text-primary text-sm underline"
              onClick={() => navigate("/login", { replace: true })}
            >
              Back to login
            </button>
          </>
        ) : (
          <>
            <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-muted-foreground">Finishing sign-in…</p>
          </>
        )}
      </div>
    </div>
  );
}
